<template>
  <!-- Dims the page behind the flyout and closes it on click (Gap 8). Always rendered so it can
       cross-fade with the panel; `pointer-events: none` while hidden means it never intercepts
       anything when the cart is closed. `aria-hidden` because it is a decorative duplicate of the
       header's back button — keyboard users get Escape (Gap 19) and that button. -->
  <div
    v-if="showScrim"
    class="cart-sidebar-scrim"
    :class="{ show: open }"
    aria-hidden="true"
    @click="$emit('close')"
  />
  <!-- `interact` fires on a deliberate act inside the panel, so a consumer running the unlock sequence
       can settle it (open questions §3.4). Emitted unconditionally rather than gated on a sequence the
       panel knows nothing about — `settle()` is a no-op when nothing is running, and keeping the
       panel presentational is the same seam that lets the harness drive it.

       Capture phase, because the header's own back button stops at `close` and a click on a Quick Add
       is handled by the slot's own component; neither would bubble a usable signal up here. -->
  <!-- `role="dialog"` follows the house pattern every other modal here uses (`PortfolioTrackerModal`,
       `PackageBuilderModal`, `TermsAcceptanceSheet`): dialog + `aria-modal` + `aria-labelledby` on the
       heading. The flyout is the one that never had it, and once focus is trapped it needs it — a
       trapped screen-reader user with no dialog semantics is told nothing about where they are.

       **`inert` while closed is a fix, not a formality.** This panel is always mounted and hidden by a
       transform, which hides it from the eye and from nothing else: its buttons stay tabbable and its
       contents stay in the accessibility tree, so on every page in the app a keyboard user could tab
       off the end of the content into an off-screen cart. `aria-modal` is bound for the same reason —
       a closed panel must not claim to be the modal thing on the page. -->
  <OnClickOutside
    ref="panelRoot"
    @trigger="handleClickOutside"
    class="sidebar"
    :class="{ show: open }"
    role="dialog"
    :aria-modal="open"
    :aria-labelledby="headingId"
    :inert="!open"
    @pointerdown.capture="handleInteract"
    @wheel.capture="handleInteract"
    @keydown.capture="handleInteract"
  >
    <div class="cart-sidebar-header">
      <button
        class="cart-sidebar-header__back"
        :aria-label="cartSidebarContent.closeCartAriaLabel"
        @click="$emit('close')"
      >
        <FontAwesomeIcon :icon="appIcons.faArrowLeft" />
      </button>
      <h2
        :id="headingId"
        class="cart-sidebar-header__title"
      >
        {{ cartSidebarContent.myCartHeading }}
      </h2>
      <div class="cart-sidebar-header__count">
        <span
          class="cart-sidebar-header__count-value"
          aria-hidden="true"
          >{{ itemCount }}</span
        >
        <FontAwesomeIcon
          class="cart-sidebar-header__count-icon"
          :icon="appIcons.faCartShopping"
          aria-hidden="true"
        />
        <span class="visually-hidden">{{ itemCountLabel }}</span>
      </div>
    </div>
    <!-- The unlock announcement (Gap 19). `role="status"` is implicitly polite and waits for a pause,
         which is right for a celebration: it is good news, not an interruption. Always rendered, so
         the region exists before it has anything to say — a live region inserted *with* its text is
         frequently not announced at all. -->
    <div
      class="visually-hidden"
      role="status"
    >
      {{ announcement }}
    </div>
    <!-- Slotted rather than wrapped: the tracker carries `.cart-sidebar__tracker` on its own root, so a
         legacy catalog (`isExpanded` false) renders a comment node and the spacing simply doesn't apply.
         A wrapper div would always exist and leave a 20px phantom gap where the tracker isn't. -->
    <slot name="tracker" />
    <div class="cart-sidebar__body">
      <!-- Directly below the tracker and above the items, per the board (`15999:29170` sits first in
           the body on both the populated and empty frames) — Gap 10. Decoupled from Quick Adds: the two
           used to be `v-if`/`v-else`, so a rendered banner suppressed YMAL entirely. They coexist. -->
      <slot name="callout" />
      <AppCartSidebarEmptyState v-if="!hasItems" />
      <template v-else>
        <div class="cart-sidebar__items">
          <slot name="items" />
        </div>
        <slot name="quickAdds" />
      </template>
      <!-- Flows inline as the body's last item — the board's empty-cart frame (`15894:29895`) puts it
           directly under the empty state with ~470px of blank panel below, which a pinned bar could not
           produce. Rendered regardless of empty/populated so the `$0.00` state shows; the `v-if` guards
           the *unloaded* cart, since `subtotal` is null only before cart data arrives and an empty
           loaded cart yields "0.00". -->
      <div
        v-if="subtotal"
        class="cart-sidebar__total"
      >
        <div class="cart-sidebar__total-label">
          <div class="cart-sidebar__total-heading">{{ orderSummaryContent.totalLabel }}</div>
          <div class="cart-sidebar__total-subtext">
            {{ orderSummaryContent.shippingAndTaxesAtCheckoutNote }}
          </div>
        </div>
        <div class="cart-sidebar__total-amount">${{ subtotal }}</div>
      </div>
    </div>
  </OnClickOutside>
</template>

<script lang="ts">
import {
  type ComponentPublicInstance,
  computed,
  defineComponent,
  getCurrentInstance,
  type PropType,
  ref
} from 'vue'

import { OnClickOutside } from '@vueuse/components'
import { onKeyStroke } from '@vueuse/core'

import useAppIcons from '@/composables/appIcons'
import { substitute } from '@/composables/contentful'
import { useFocusTrap } from '@/composables/useFocusTrap'

import AppCartSidebarEmptyState from './AppCartSidebarEmptyState.vue'
import { cartSidebarContent } from '@/content/cartSidebar'
import { orderSummaryContent } from '@/content/orderSummary'

/**
 * Presentational cart-flyout panel — the scrim, the drawer, the header, the scrolling body and the
 * Total. Every value it renders arrives as a prop and everything inside the body arrives through a
 * slot, so it reads no store and performs no mutation.
 *
 * **Why it is separate from `AppCartSidebar`.** The debug harness (Gap 13) drives the flyout from a
 * synthetic ladder and synthetic rows, and it must drive *this* panel rather than a copy of it —
 * otherwise the beat sheet for design sign-off validates something other than what ships, and a fix
 * to one shell silently misses the other. `AppCartSidebar` is the store-bound wrapper that feeds this
 * from the cart; the harness is a second caller feeding it fixtures. Same seam as
 * `RewardsTracker` / `RewardsTrackerFlyout`.
 *
 * The layer, scroll model and Total placement are all documented in the styles below.
 */
export default defineComponent({
  name: 'AppCartSidebarPanel',
  components: { OnClickOutside, AppCartSidebarEmptyState },
  props: {
    /** Drawer visibility — drives the panel transform and the scrim cross-fade. */
    open: {
      type: Boolean,
      default: false
    },
    /** Count shown in the header pill. Renders at every count including zero, per the board. */
    itemCount: {
      type: Number,
      default: 0
    },
    /**
     * Pre-formatted subtotal string, or `null` while the cart is unloaded. Null (not `"0.00"`) is what
     * keeps the Total from flashing during load — an empty *loaded* cart shows `$0.00` by design.
     */
    subtotal: {
      type: String as PropType<string | null>,
      default: null
    },
    /** False renders the empty state in place of the items and Quick Adds. */
    hasItems: {
      type: Boolean,
      default: false
    },
    /** Off lets the harness show the panel without the page dim. Always on in the real flyout. */
    showScrim: {
      type: Boolean,
      default: true
    },
    /**
     * Off keeps a click outside the panel from closing it. The harness needs that — its own controls
     * sit outside the drawer, and every click on one would otherwise dismiss the thing being inspected.
     */
    closeOnClickOutside: {
      type: Boolean,
      default: true
    },
    /** Announced to assistive technology when it changes. Empty renders an empty live region. */
    announcement: {
      type: String,
      default: ''
    },
    /**
     * Off leaves Tab free to reach the page behind the panel. On in the real flyout, where the scrim
     * makes that page non-interactive anyway.
     *
     * The harness needs it off, and for a sharper reason than its sibling props: `focus-trap` pulls
     * focus back the moment it escapes the container — `allowOutsideClick` only stops the *click*
     * being cancelled, not the focus being reclaimed. So with the drawer open, every text field in the
     * harness's controls would blur on its first keystroke. The sandbox's panel is deliberately not
     * modal; this is the third face of the same fact as `showScrim` and `closeOnClickOutside`.
     */
    trapFocus: {
      type: Boolean,
      default: true
    }
  },
  emits: ['close', 'interact'],
  setup(props, { emit }) {
    // Escape closes the drawer (Gap 19). `onKeyStroke` matches the convention `useFocusTrap` records
    // — "each modal owns its own Escape handler" — rather than routing through the trap, which is
    // configured with `escapeDeactivates: false` for exactly that reason.
    //
    // Guarded on `open` because the panel is always mounted, hidden by transform rather than `v-if`,
    // so an unguarded handler would swallow Escape on every page whether the cart was showing or not.
    //
    // Known interaction, inherent to that convention: a modal opened over the flyout also handles
    // Escape, so one press closes both. Acceptable here — every layer in this app already behaves
    // that way, and the alternative is a modal registry no gap in this feature authorises.
    onKeyStroke('Escape', () => {
      if (props.open) emit('close')
    })

    // Focus management on auto-open (Gap 19, A6b). It covers the customer's own opens too — once the
    // scrim is up the drawer is modal however it got there — but auto-open is what made it necessary
    // and what makes the *return* half load-bearing: the sequence closes the cart again six seconds
    // later, and without `returnFocusOnDeactivate` (the shared composable's inherited default) a
    // keyboard customer would be dropped on `<body>` by an act they never performed.
    //
    // It is also what finally makes Gap 8 true for the keyboard. The scrim stops the mouse reaching
    // the page behind; nothing stopped Tab.
    //
    // `$el` because `OnClickOutside` is a component — it renders one root element, which is the
    // drawer, so the trap's container is the same node the styles and the transform are on.
    const panelRoot = ref<ComponentPublicInstance | null>(null)
    const panelElement = computed<HTMLElement | null>(
      () => (panelRoot.value?.$el as HTMLElement | undefined) ?? null
    )
    useFocusTrap(panelElement, () => props.open && props.trapFocus)

    // The whole phrase, so the screen reader doesn't depend on a space between two spans
    const itemCountLabel = computed(() =>
      props.itemCount === 1
        ? cartSidebarContent.itemCountLabelOne
        : substitute(cartSidebarContent.itemCountLabelOtherTemplate, { Count: props.itemCount })
    )

    return {
      cartSidebarContent,
      itemCountLabel,
      orderSummaryContent,
      ...useAppIcons(),
      panelRoot,
      // Ties `aria-labelledby` to the visible "My Cart" heading rather than restating it as an
      // `aria-label`, matching the other modals. Per-instance because the harness mounts a second one.
      headingId: `cart-sidebar-heading-${getCurrentInstance()?.uid ?? 0}`
    }
  },
  methods: {
    handleClickOutside() {
      if (this.closeOnClickOutside) {
        this.$emit('close')
      }
    },
    /**
     * A deliberate act inside the panel: a press, a scroll, a key.
     *
     * **Hover is deliberately not one of them**, though the business ruling says "scroll, click, hover"
     * (open questions §3.4). The drawer occupies the right ~820px of the viewport and slides in over
     * wherever the pointer already is, so `mouseenter` would fire for anyone whose cursor happened to
     * be in that band when the cart opened — killing the animation before it played, most often for
     * the customer who just clicked an add button on the right of the page. Reaching for the cart is
     * the signal worth acting on; the pointer merely being somewhere is not. Raised rather than
     * assumed — if Carla wants hover too, it wants a movement threshold and a delay, not `mouseenter`.
     *
     * Guarded on `open` because the panel is always mounted, hidden by transform rather than `v-if` —
     * an unguarded handler would report interactions with whatever is behind the closed drawer.
     */
    handleInteract() {
      if (this.open) {
        this.$emit('interact')
      }
    }
  }
})
</script>

<style lang="scss" scoped>
// Page scrim — Figma `15980:29322`, a flat `rgba(0, 0, 0, 0.25)` over the whole page.
//
// The board's 25% is the value used, not the prototype's "page dim to 40%" (guide §3). Opacity is a
// visual property, and the artifact ruling gives the static board authority over anything visual —
// the prototype governs motion and timing only.
//
// The fade duration matches `.sidebar`'s existing `0.3s` so the two move together. The board's
// sequence puts the drawer-in at 500ms (`DRAWER_IN_MS`); retiming both belongs to A1, where the
// timings module lands, rather than hardcoding a second magic number here.
//
// **Layer (decided 2026-08-04).** The flyout is an offcanvas drawer, so it takes Bootstrap's offcanvas
// slot: scrim `1040`, panel `1045`. Those land exactly in this app's existing gap — page chrome
// (`StorefrontFooter`, `PriceRuleBanner`) sits at `1030` and every modal at `1050`–`1051`, so the
// drawer now covers the chrome it must while still yielding to modals.
//
// The flyout was previously `10`, *below* page chrome, which is why `footerInset` existed to pad the
// scroll area clear of the page footer. That machinery is gone with this change.
//
// `BackToTop` (`10000`) is the one thing still above the scrim; it is hidden via the
// `cart-flyout-open` body class in `main.scss` rather than escalating the drawer past the modal tier.
//
// `OnClickOutside` is kept as a belt-and-braces close path even though the scrim now covers the page.
.cart-sidebar-scrim {
  position: fixed;
  inset: 0;
  z-index: 1040;
  background-color: rgba(0, 0, 0, 0.25);
  opacity: 0;
  pointer-events: none;
  // Moves with the panel, so it takes the panel's duration. A plain `ease` rather than the drawer's
  // spring approximation — an overshoot curve on an opacity fade would read as a flicker.
  transition: opacity 500ms ease;

  &.show {
    opacity: 1;
    pointer-events: auto;
  }
}

// **Why the sequence needs no transition-start guard, having asked the question.** The drawer's slide
// is the sequence's first beat, so the machine's clock and this transition must start together —
// and a class flipped in the same tick an element first paints normally skips its transition
// entirely, leaving the clock to run a 500ms drawer beat over a drawer that already arrived. That
// does not happen here: `.sidebar` is always mounted and hidden by transform rather than `v-if`, so
// its pre-transition style is computed at mount and `show` always has something to interpolate from.
// Recorded because the next person to wire an opening animation will ask, and because it stops being
// true the moment someone "optimises" the panel behind a `v-if`.

// Reduced motion: A5 routes the machine through `settle()`; this is the CSS half.
@media (prefers-reduced-motion: reduce) {
  .cart-sidebar-scrim,
  .sidebar {
    transition: none;
  }
}

.sidebar {
  position: fixed;
  top: 0;
  right: 0;
  height: 100%;
  // Full-width on small screens; up to 820px (the desktop flyout width, Figma `15980:29577`) once
  // the viewport allows — `min()` resolves to 820px above that and the viewport width below it.
  //
  // `100%`, not `100vw`. For a fixed element the percentage resolves against the initial containing
  // block, which excludes the classic scrollbar; `100vw` includes it. Below 820px the two differ by
  // the scrollbar's width, so `100vw` made the panel wider than the space available and pushed a
  // horizontal scrollbar onto the page underneath it.
  width: min(820px, 100%);
  // The panel is white throughout — header, tracker and body (Figma `15894:29795` sets no body fill).
  background-color: $cady-color-white;
  // Figma `15980:29578` "Overlay+Shadow".
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  transform: translateX(100%);
  // `DRAWER_IN_MS` / `DRAWER_EASING` from `lib/rewardSequenceTimings.ts`. Was 0.3s ease, which S6
  // flagged as owing a retime once the timings module existed: the sequence schedules the fill 200ms
  // after the drawer is supposed to have settled, so a drawer running 300ms put a 200ms gap where the
  // spec wants none, and one running longer would have started the fill under a moving panel.
  //
  // The easing approximates the spec's `spring(stiffness: 290, damping: 28)` — ζ ≈ 0.82, ~1% overshoot
  // settling around 330ms — which a cubic-bezier reproduces within perceptual tolerance. No physics
  // library, and the spring's numbers live in the timings module as provenance.
  transition: transform 500ms cubic-bezier(0.22, 1, 0.36, 1);
  z-index: 1045;
  // The panel itself no longer scrolls — header, tracker and footer are pinned and only the body
  // scrolls, which is the board's structure (`15980:29696` is a footer bar outside the scroll area).
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

// Header and tracker hold their height; the body takes the remainder and scrolls.
//
// Keeping these two pinned is a judgement call, not a board reading — a static frame can't express
// stickiness either way. The reason is the unlock animation: it fills the tracker's rail, so the
// tracker scrolling out of view would hide the payoff. Overrule freely if the sequence work disagrees.
.cart-sidebar-header,
:slotted(.cart-sidebar__tracker) {
  flex-shrink: 0;
}

.sidebar.show {
  transform: translateX(0);
}

// Figma mobile `15927:54421` / desktop `15980:29579`.
//
// The board draws padding-bottom 17px (mobile) and 25px (desktop) with the 1px border painted
// *inside* that padding, netting the 69px / 101px frame heights. Under `box-sizing: border-box` the
// border sits outside the padding box, so symmetric 16px / 24px padding plus the 1px border
// reproduces the same totals — 16 + 36 + 16 + 1 = 69, and 24 + 52 + 24 + 1 = 101.
.cart-sidebar-header {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  padding: 1rem;
  margin-bottom: 1.25rem; // 20px gap to the rewards tracker — see the S1 deferral note below
  border-bottom: 1px solid $cady-color-gray-96;

  @include media-breakpoint-up(md) {
    padding: 1.5rem;
  }

  &__back {
    justify-self: start;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 48px;
    background-color: $cady-color-gray-100;
    color: $cady-color-black;
    // Sizes the Font Awesome glyph, which renders at 1em.
    font-size: 1rem; // 16px
    cursor: pointer;

    @include media-breakpoint-up(md) {
      width: 52px;
      height: 52px;
      font-size: 1.25rem; // 20px
    }
  }

  &__title {
    margin: 0;
    text-align: center;
    // Pinned explicitly rather than inherited from Bootstrap's responsive `h2` ramp, which lands
    // near these values by coincidence and would drift if that ramp ever moves.
    font-size: $cady-type-h3-mobile-size;
    font-weight: $cady-type-heading-weight;
    line-height: $cady-type-heading-line-height;
    color: $cady-color-black;

    @include media-breakpoint-up(md) {
      font-size: $cady-type-h3-desktop-size;
    }
  }

  // Display-only count pill — Figma mobile `15927:54424` / desktop `15980:29582`, which carries the
  // annotation "Shows # of items in cart." It replaces the bulk-empty button that previously carried
  // the count (Gap 2), so there is no interactive or disabled state, and the bulk `remove_from_cart`
  // GTM event went with the button; per-row removal still fires its own. Renders at every count
  // including zero — the board draws it in the empty-cart frames (`15894:29790` / `15894:29906`).
  &__count {
    justify-self: end;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.25rem; // 4px
    min-height: 36px;
    border-radius: 48px;
    padding: 0.375rem 0.75rem; // 6px 12px
    background-color: $cady-color-black;
    color: $cady-color-white;

    @include media-breakpoint-up(md) {
      // 12px padding around a 24px number and a 20px icon box nets the board's 80x52 pill.
      min-width: 80px;
      padding: 0.75rem; // 12px
    }
  }

  &__count-value {
    font-size: $cady-type-h5-mobile-size;
    font-weight: $cady-type-heading-weight;
    line-height: $cady-type-heading-line-height;

    @include media-breakpoint-up(md) {
      font-size: $cady-type-h5-desktop-size;
    }
  }

  &__count-icon {
    font-size: 0.75rem; // 12px

    @include media-breakpoint-up(md) {
      font-size: 1rem; // 16px
    }
  }
}

// Tracker sits in the white top zone; needs its own horizontal padding (the sidebar has none).
//
// S1 deferral: the board (`15980:30164` / `16066:33179`) seats the tracker flush against the header
// with the rail inset 45px (desktop) / 21px (mobile) rather than the 24px here, and gives the
// tracker frame its own internal vertical padding that `RewardsTracker.vue` does not have. Changing
// the wrapper inset therefore resizes the rail — a tracker visual change that no gap in this
// feature covers, off geometry flagged as unreliable during extraction (the board's tracker
// internals overflow their frame). Left as-is; reconciled at S7 with the rest of the panel states.
//
// `:slotted()` because the class lands on the caller's component root — see the template note.
:slotted(.cart-sidebar__tracker) {
  display: block;
  padding: 0 1.5rem;
  margin-bottom: 1.25rem;
}

// Holds the cart rows and the synthesized Free Shipping row, and nothing else. The wrapper is what
// makes the row divider's `:not(:last-child)` mean "between rows" — without it the last row's next
// sibling is the Free Gift callout, so every row would draw a divider. `flow-root` keeps the rows'
// margins from collapsing out of the list.
.cart-sidebar__items {
  display: flow-root;
}

// Total (Figma `15980:29696` desktop / `15927:54522` mobile): "Total" + "Shipping & taxes…" subtext
// left, amount right. Gap 7 — shown at both breakpoints now, where it was desktop-only.
//
// Flows as the body's last item rather than pinned to the panel bottom. S8 pinned it on a misreading of
// the extraction — the layer is named "Background+HorizontalBorder", which reads like a bar — but the
// board's empty-cart frame settles it: the Total sits directly under the empty state with ~470px of
// blank panel below, which a pinned bar cannot produce. The board's populated and empty frames only
// disagree about this on a short cart, which is why the populated frame alone looked like a bar.
//
// Accepted consequence (guide, Gap 7): on mobile the subtotal also appears in the fixed footer's CTA
// behind the panel, so the figure is on screen twice.
.cart-sidebar__total {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;

  @include media-breakpoint-up(md) {
    align-items: center;
  }

  &-label {
    display: flex;
    flex-direction: column;
    gap: 0.25rem; // 4px
    min-width: 0;
  }

  &-heading {
    font-size: $cady-type-h3-mobile-size;
    font-weight: $cady-type-heading-weight;
    line-height: $cady-type-heading-line-height;
    color: $cady-color-black;

    @include media-breakpoint-up(md) {
      font-size: $cady-type-h4-desktop-size;
    }
  }

  &-subtext {
    font-size: $cady-type-body-sm-size;
    line-height: $cady-type-body-sm-line-height;
    color: $cady-color-gray-600;

    @include media-breakpoint-up(md) {
      font-size: $cady-type-body-nm-size;
      line-height: $cady-type-body-nm-line-height;
    }
  }

  &-amount {
    flex-shrink: 0;
    font-size: $cady-type-h3-mobile-size;
    font-weight: $cady-type-heading-weight;
    line-height: $cady-type-heading-line-height;
    color: $cady-color-black;
    white-space: nowrap;

    @include media-breakpoint-up(md) {
      font-size: $cady-type-h4-desktop-size;
    }
  }
}

// Everything below the tracker — full-bleed white, grows to fill the flyout height. Figma
// `15894:29795` (desktop, 24px/48px) and `15927:54429` (mobile, 24px top / 32px bottom / 16px sides).
// The only scrolling region in the panel. `min-height: 0` is what lets a flex child actually scroll
// rather than growing to fit its content.
//
// Section spacing is the board's flex-column `gap` (Figma `15894:29795` 40px desktop / `15927:54429`
// 32px mobile). S1 applied those as per-child margins because a `v-if`/`v-else` wrapper sat between the
// body and its sections; that wrapper is gone, so this is now the `gap` the board actually specifies.
.cart-sidebar__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  // Hold the scrollbar's gutter whether or not it is scrolling.
  //
  // Two reasons, and the second is the one that produced a bug report. **Layout:** the body crosses
  // the scroll threshold *during* the sequence — reward rows arrive at the 4,000ms beat — so without
  // this the whole panel's contents jump a scrollbar's width mid-celebration. **Paint:** on the way
  // back down, a cart emptied of its rewards left a strip of page content showing through where the
  // scrollbar had been. The panel carries a `transform`, so it composites on its own layer, and the
  // band vacated by a disappearing scrollbar was not being repainted. Reserving the gutter means
  // nothing is ever vacated, which fixes the artifact at its cause rather than papering the strip.
  scrollbar-gutter: stable;
  display: flex;
  flex-direction: column;
  gap: 2rem; // 32px
  padding: 1.5rem 1rem 2rem;
  background-color: $cady-color-white;

  @include media-breakpoint-up(md) {
    gap: 2.5rem; // 40px
    padding: 1.5rem 3rem;
  }
}
</style>
